import dateHelper from '@/utils/dateHelper'
import moment from 'moment'
import { computed, ref, watch } from 'vue'

export type AdminPeriodType = 'month' | 'quarter' | 'year' | 'custom'

export interface AdminPeriodRange {
  startDate: string
  endDate: string
  label: string
  periodType: AdminPeriodType
}

export interface MonthOption {
  label: string
  value: string
  startDate: string
  endDate: string
}

export interface QuarterOption {
  label: string
  value: string
  quarter: number
  year: number
  startDate: string
  endDate: string
}

export interface YearOption {
  label: string
  value: number
  startDate: string
  endDate: string
}

function toUtcStartOfDay(date: Date): string {
  return dateHelper.userTimezoneToUTC(moment(date).startOf('day').format(dateHelper.getIsoFormat()))
}

function toUtcEndOfDay(date: Date): string {
  return dateHelper.userTimezoneToUTC(moment(date).endOf('day').format(dateHelper.getIsoFormat()))
}

function buildMonthOptions(): MonthOption[] {
  const months: MonthOption[] = []
  const now = new Date()

  for (let i = 0; i < 24; i++) {
    const startOfMonth = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() - i + 1, 0)

    months.push({
      label: startOfMonth.toLocaleString('default', { month: 'long', year: 'numeric' }),
      value: moment(startOfMonth).format('YYYY-MM'),
      startDate: toUtcStartOfDay(startOfMonth),
      endDate: toUtcEndOfDay(endOfMonth)
    })
  }

  return months
}

function buildQuarterOptions(): QuarterOption[] {
  const quarters: QuarterOption[] = []
  const now = new Date()
  const currentYear = now.getFullYear()
  const currentQuarter = Math.floor(now.getMonth() / 3) + 1

  for (let yearOffset = 0; yearOffset < 5; yearOffset++) {
    const year = currentYear - yearOffset
    const maxQuarter = yearOffset === 0 ? currentQuarter : 4

    for (let quarter = maxQuarter; quarter >= 1; quarter--) {
      const startMonth = (quarter - 1) * 3
      const startOfQuarter = new Date(year, startMonth, 1)
      const endOfQuarter = new Date(year, startMonth + 3, 0)

      quarters.push({
        label: `Q${quarter} ${year}`,
        value: `${year}-Q${quarter}`,
        quarter,
        year,
        startDate: toUtcStartOfDay(startOfQuarter),
        endDate: toUtcEndOfDay(endOfQuarter)
      })
    }
  }

  return quarters
}

function buildYearOptions(): YearOption[] {
  const years: YearOption[] = []
  const currentYear = new Date().getFullYear()

  for (let i = 0; i < 5; i++) {
    const year = currentYear - i
    const startOfYear = new Date(year, 0, 1)
    const endOfYear = new Date(year, 11, 31)

    years.push({
      label: year.toString(),
      value: year,
      startDate: toUtcStartOfDay(startOfYear),
      endDate: toUtcEndOfDay(endOfYear)
    })
  }

  return years
}

function buildPeriodRange(
  periodType: AdminPeriodType,
  startDate: string,
  endDate: string,
  label: string
): AdminPeriodRange {
  return {
    startDate,
    endDate,
    label,
    periodType
  }
}

export function useAdminPeriod() {
  const periodType = ref<AdminPeriodType>('month')
  const monthOptions = ref<MonthOption[]>(buildMonthOptions())
  const quarterOptions = ref<QuarterOption[]>(buildQuarterOptions())
  const yearOptions = ref<YearOption[]>(buildYearOptions())

  const selectedMonth = ref(monthOptions.value[0]?.value ?? '')
  const selectedQuarter = ref(quarterOptions.value[0]?.value ?? '')
  const selectedYear = ref(yearOptions.value[0]?.value ?? new Date().getFullYear())

  const customStartDate = ref<Date | null>(moment().startOf('month').toDate())
  const customEndDate = ref<Date | null>(new Date())

  const periodRange = computed<AdminPeriodRange>(() => {
    if (periodType.value === 'month') {
      const month = monthOptions.value.find((m) => m.value === selectedMonth.value) ?? monthOptions.value[0]
      return buildPeriodRange('month', month.startDate, month.endDate, month.label)
    }

    if (periodType.value === 'quarter') {
      const quarter =
        quarterOptions.value.find((q) => q.value === selectedQuarter.value) ?? quarterOptions.value[0]
      return buildPeriodRange('quarter', quarter.startDate, quarter.endDate, quarter.label)
    }

    if (periodType.value === 'year') {
      const year = yearOptions.value.find((y) => y.value === selectedYear.value) ?? yearOptions.value[0]
      return buildPeriodRange('year', year.startDate, year.endDate, year.label)
    }

    const start = customStartDate.value ?? new Date()
    const end = customEndDate.value ?? start
    const safeEnd = end < start ? start : end
    const startUtc = toUtcStartOfDay(start)
    const endUtc = toUtcEndOfDay(safeEnd)
    const label = `${dateHelper.formatDate(startUtc)} – ${dateHelper.formatDate(endUtc)}`

    return buildPeriodRange('custom', startUtc, endUtc, label)
  })

  const periodDurationDays = computed(() => {
    const start = moment(dateHelper.utcToUserTimezone(periodRange.value.startDate)).startOf('day')
    const end = moment(dateHelper.utcToUserTimezone(periodRange.value.endDate)).startOf('day')
    return end.diff(start, 'days') + 1
  })

  watch(customEndDate, (end) => {
    if (end && customStartDate.value && end < customStartDate.value) {
      customEndDate.value = customStartDate.value
    }
  })

  return {
    periodType,
    monthOptions,
    quarterOptions,
    yearOptions,
    selectedMonth,
    selectedQuarter,
    selectedYear,
    customStartDate,
    customEndDate,
    periodRange,
    periodDurationDays
  }
}
